import type { SongMarker, MetronomeSettings } from './types';

/** The text actually shown on the Performance mode progress bar for a
 * marker: its explicit shortLabel if the user set one, otherwise a truncated
 * uppercase fallback derived from the full label. Labels are freeform, so
 * there is no reliable automatic abbreviation ("Bridge A" doesn't truncate to
 * "BRA") — the explicit field lets the user choose one when it matters. */
export function effectiveShortLabel(marker: SongMarker): string {
  if (marker.shortLabel.trim()) return marker.shortLabel.trim().slice(0, 4).toUpperCase();
  const trimmed = marker.label.trim();
  if (!trimmed) return '···';
  return trimmed.slice(0, 3).toUpperCase();
}

/** Where to actually seek when a marker is tapped in Practice Mode: some
 * number of seconds BEFORE the marker's own timestamp, so the rest of the
 * band gets a run-up into the section instead of a cold start exactly on
 * the boundary, rather than the marker's raw timestamp. Clamped to 0 since
 * there's nothing before the start of the song. */
export function markerSeekTarget(markerTimeSeconds: number, leadInSeconds: number): number {
  return Math.max(0, markerTimeSeconds - leadInSeconds);
}

/** Seconds per beat for a song's metronome settings, same convention as
 * MetronomeEngine's own private helper — beatUnit 8 means an eighth note
 * gets the beat (e.g. 6/8), so it's half a quarter-note's duration. Shared
 * here (rather than duplicated) because the lead-in's beat-count picker
 * needs the exact same conversion the actual click engine uses, or a
 * "4-count" wouldn't line up with what the drummer actually hears. */
export function secondsPerBeat(settings: Pick<MetronomeSettings, 'bpm' | 'beatUnit'>): number {
  const quarterNoteSeconds = 60 / settings.bpm;
  return settings.beatUnit === 8 ? quarterNoteSeconds / 2 : quarterNoteSeconds;
}
